import { IconCircleCheck, IconPlus } from '@tabler/icons-react'
import { FormProps } from 'antd'
import styled from 'styled-components'

import { CaptureBlock, TemplateData } from '../templates/types/blueprint'

import { ActionAIGenerator } from './ActionAIGenerator'
import { ActionFormItems } from './ActionFormItems'
import { Field, FormFieldValues } from './actionHooks'

import { Button } from '@/components/shared-components/BUI/Button'
import { Form, HorizontalForm } from '@/components/shared-components/BUI/Form'
import { Input } from '@/components/shared-components/BUI/Input'
import { Flexbox } from '@/components/shared-components/Flexbox'
import { ResponsiveDrawer } from '@/components/shared-components/ResponsiveDrawer'
import { usePreventSaving } from '@/context/preventSavingContext'
import { useUser } from '@/context/userContext'

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

type Props = {
  formProps?: Omit<FormProps, 'form'>
  open: boolean
  onClose: () => void
  isPublic?: boolean
  builderId: string
  loading?: boolean
  contextualDate?: string
  templateBlockData?: CaptureBlock
  templateResponseData?: TemplateData
  templateResponseId?: string
}

export const CreateActionDrawer: React.FC<Props> = ({
  formProps,
  open,
  onClose,
  isPublic,
  builderId,
  loading,
  contextualDate,
  templateBlockData,
  templateResponseId,
}) => {
  const [form] = Form.useForm()
  const { isSavingBlocked } = usePreventSaving()
  const user = useUser()
  const aiTools = user?.aiTools

  const handleSubmit = (values: FormFieldValues) => {
    formProps?.onFinish?.(values)
    form.resetFields()
  }

  const submitAndContinue = () => {
    form.setFieldValue(Field.Continue, true)
    form.submit()
  }

  const submitAndDone = () => {
    form.setFieldValue(Field.Continue, false)
    form.submit()
  }

  return (
    <ResponsiveDrawer
      maxWidth={500}
      title={
        aiTools?.contextualActionGeneratorEnabled && templateBlockData ? (
          <TitleWrapper>
            Create action{' '}
            <ActionAIGenerator
              builderId={builderId}
              captureBlockId={templateBlockData.id}
              onSuccess={(data) => {
                form.setFieldsValue({
                  [Field.Title]: data.title,
                  [Field.Description]: data.description,
                  [Field.PriorityType]: data.priority,
                  [Field.Type]: data.type,
                })
              }}
              templateResponseId={templateResponseId}
            />
          </TitleWrapper>
        ) : (
          'Create action'
        )
      }
      open={open}
      onClose={onClose}
      destroyOnClose // clean up the form items
      footer={
        <Flexbox gap="1rem" justifyContent="space-between">
          <Button
            variant="primary"
            icon={<IconCircleCheck size={16} />}
            onClick={submitAndDone}
            loading={loading || isSavingBlocked}
          >
            Save &amp; finish
          </Button>
          <Button
            icon={<IconPlus size={16} />}
            onClick={submitAndContinue}
            loading={loading || isSavingBlocked}
          >
            Save &amp; add more
          </Button>
        </Flexbox>
      }
    >
      <HorizontalForm form={form} {...formProps} onFinish={handleSubmit}>
        <ActionFormItems
          isPublic={isPublic}
          builderId={builderId}
          contextualDate={contextualDate}
        />
        <Form.Item noStyle name={Field.Continue}>
          <Input type="hidden" />
        </Form.Item>
      </HorizontalForm>
    </ResponsiveDrawer>
  )
}
